import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import './EducationProgramHeroForm.css';

const ICON_OPTIONS = [
  { value: 'fa-user-tie', label: 'User Tie', icon: 'fa-user-tie' },
  { value: 'fa-users', label: 'Users', icon: 'fa-users' },
  { value: 'fa-book', label: 'Book', icon: 'fa-book' },
  { value: 'fa-home', label: 'Home', icon: 'fa-home' },
  { value: 'fa-handshake', label: 'Handshake', icon: 'fa-handshake' },
  { value: 'fa-heart', label: 'Heart', icon: 'fa-heart' },
  { value: 'fa-brain', label: 'Brain', icon: 'fa-brain' },
  { value: 'fa-star', label: 'Star', icon: 'fa-star' },
  { value: 'fa-lightbulb', label: 'Light Bulb', icon: 'fa-lightbulb' },
  { value: 'fa-graduation-cap', label: 'Graduation Cap', icon: 'fa-graduation-cap' },
  { value: 'fa-trophy', label: 'Trophy', icon: 'fa-trophy' },
  { value: 'fa-shield', label: 'Shield', icon: 'fa-shield' },
  { value: 'fa-smile', label: 'Smile', icon: 'fa-smile' },
  { value: 'fa-hands-helping', label: 'Helping Hands', icon: 'fa-hands-helping' },
  { value: 'fa-comments', label: 'Comments', icon: 'fa-comments' },
  { value: 'fa-user-friends', label: 'User Friends', icon: 'fa-user-friends' }
];

export default function StudentLifeWellbeingForm({ apiBase }) {
  const toastUtil = useToast();
  const ToastContainer = toastUtil.ToastContainer;
  const toast = toastUtil;

  const [formData, setFormData] = useState({
    kicker: '',
    title: '',
    description: '',
    pillars: [],
    supports: [],
    images: []
  });

  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    description: false,
    pillars: false,
    supports: false,
    images: false
  });

  const [originalData, setOriginalData] = useState({...formData});
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [mediaMap, setMediaMap] = useState({});
  const [imageUploading, setImageUploading] = useState({});
  const [imageProgress, setImageProgress] = useState({});

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([loadMediaMap(), loadSection()]);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      console.log('[StudentLifeWellbeingForm] Fetching media map...');
      const response = await fetch(
        `${apiBase}/studentlife/cms/media?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        }
      );
      
      if (!response.ok) {
        console.warn('[StudentLifeWellbeingForm] Media fetch failed:', response.status);
        return;
      }
      
      const result = await response.json();
      
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach((media) => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path || media.filepath || '';
          
          if (mediaId && mediaUrl) {
            map[mediaId] = mediaUrl;
            map[String(mediaId)] = mediaUrl;
            map[parseInt(mediaId)] = mediaUrl;
          }
        });
        
        console.log('[StudentLifeWellbeingForm] ✓ Media map ready:', Object.keys(map).length, 'items');
        setMediaMap(map);
      }
    } catch (err) {
      console.error('[StudentLifeWellbeingForm] Media fetch error:', err.message);
    }
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      console.log('[StudentLifeWellbeingForm] Loading wellbeing section...');
      
      const response = await fetch(
        `${apiBase}/studentlife/cms/sections?_t=${Date.now()}`,
        {
          credentials: 'include',
          headers: { 'Cache-Control': 'no-cache' }
        }
      );

      if (!response.ok) {
        toast.error('Failed to load section');
        return;
      }

      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        const wellbeingSection = result.data.find(s => s.section_type === 'student-life-wellbeing');
        
        if (wellbeingSection) {
          setSectionId(wellbeingSection.id);
          
          let published = wellbeingSection.content_published;
          if (typeof published === 'string') {
            published = JSON.parse(published);
          }
          
          const enData = published?.en || {};
          const newFormData = {
            kicker: enData.kicker || '',
            title: enData.title || '',
            description: enData.description || '',
            pillars: Array.isArray(enData.pillars) ? enData.pillars : [],
            supports: Array.isArray(enData.supports) ? enData.supports : [],
            images: Array.isArray(enData.images) ? enData.images : []
          };
          
          setFormData(newFormData);
          setOriginalData(newFormData);
          console.log('[StudentLifeWellbeingForm] ✓ Section loaded:', newFormData);
        } else {
          toast.info('Wellbeing section not found in database');
        }
      }
    } catch (err) {
      console.error('[StudentLifeWellbeingForm] Load error:', err);
      toast.error('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Helper function to check if field has actual changes
  const hasFieldChanged = (fieldName) => {
    const fieldValue = formData[fieldName];
    const oldValue = originalData[fieldName];
    
    // Deep comparison for arrays and objects
    return JSON.stringify(fieldValue) !== JSON.stringify(oldValue);
  };

  const handleSubmitField = async (fieldName) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    const fieldValue = formData[fieldName];
    const oldValue = originalData[fieldName];

    if (!hasFieldChanged(fieldName)) {
      toast.info('No changes detected');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldName,
            field_name: fieldName.charAt(0).toUpperCase() + fieldName.slice(1),
            old_value: oldValue,
            new_value: fieldValue,
            locale: 'en'
          })
        }
      );

      const result = await response.json();
      console.log('[StudentLifeWellbeingForm] Submit response:', result);

      if (result.success) {
        toast.success(`${fieldName} submitted for approval`);
        setPendingFields(prev => ({ ...prev, [fieldName]: true }));
      } else {
        toast.error(result.error || 'Submission failed');
      }
    } catch (err) {
      console.error('[StudentLifeWellbeingForm] Submit error:', err);
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleImageUpload = async (imgIndex, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    const key = `img-${imgIndex}`;
    setImageUploading(prev => ({ ...prev, [key]: true }));
    setImageProgress(prev => ({ ...prev, [key]: 0 }));

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('section_type', 'student-life-wellbeing');

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = Math.round((e.loaded / e.total) * 100);
          setImageProgress(prev => ({ ...prev, [key]: percentComplete }));
        }
      });

      xhr.addEventListener('load', async () => {
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const response = JSON.parse(xhr.responseText);
            console.log('[StudentLifeWellbeingForm] Upload response:', response);

            if (response.success || response.ok) {
              const mediaId = response.media_id || response.id || response.filename;
              console.log('[StudentLifeWellbeingForm] ✓ Image uploaded, media ID:', mediaId);

              if (mediaId) {
                // Update images array
                const newImages = [...formData.images];
                newImages[imgIndex] = mediaId;
                handleChange('images', newImages);

                // Update mediaMap locally
                setMediaMap(prev => ({
                  ...prev,
                  [mediaId]: response.url || response.file_path || `/api/uploads/studentlife/${response.filename}`
                }));

                console.log('[StudentLifeWellbeingForm] Images array updated:', newImages);
                toast.success('✓ Image uploaded (ID: ' + mediaId + ')');
              } else {
                toast.error('No media ID in response');
              }
            } else {
              toast.error(response.error || 'Upload failed');
            }
          } catch (parseErr) {
            console.error('[StudentLifeWellbeingForm] JSON parse error:', parseErr);
            toast.error('Invalid server response');
          }
        } else {
          console.error('[StudentLifeWellbeingForm] Upload failed with status', xhr.status);
          toast.error('Upload failed (HTTP ' + xhr.status + ')');
        }
        setImageUploading(prev => ({ ...prev, [key]: false }));
      });

      xhr.addEventListener('error', () => {
        console.error('[StudentLifeWellbeingForm] Upload network error');
        toast.error('Network error during upload');
        setImageUploading(prev => ({ ...prev, [key]: false }));
      });

      xhr.open('POST', `${apiBase}/studentlife/cms/media/upload`);
      xhr.withCredentials = true;
      xhr.send(formDataUpload);
    } catch (err) {
      console.error('[StudentLifeWellbeingForm] Upload setup error:', err);
      toast.error('Upload setup error: ' + err.message);
      setImageUploading(prev => ({ ...prev, [key]: false }));
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <div className="spinner" />
        <p>Loading Wellbeing section...</p>
      </div>
    );
  }

  return (
    <div className="education-hero-form-container">
      <ToastContainer />
      
      <div className="hero-form-wrapper">
        <div className="hero-form-header">
          <h2 className="hero-form-title">Wellbeing & Social Emotional Learning</h2>
          <p className="hero-form-description">Manage wellbeing section content, pillars, support pathways, and images</p>
        </div>

      {/* KICKER */}
      <div className="form-field-card">
        <label className="form-label">
          Kicker Text
          {pendingFields.kicker && <span className="badge-pending">Pending Approval</span>}
        </label>
        <input
          type="text"
          className="form-input"
          value={formData.kicker}
          onChange={(e) => handleChange('kicker', e.target.value)}
          placeholder="e.g., Wellbeing & Social Emotional Learning"
        />
        <button
          className="btn-primary"
          onClick={() => handleSubmitField('kicker')}
          disabled={submitting.kicker || pendingFields.kicker || !hasFieldChanged('kicker')}
        >
          {submitting.kicker ? 'Submitting...' : 'Submit for Approval'}
        </button>
      </div>

      {/* TITLE */}
      <div className="form-field-card">
        <label className="form-label">
          Title
          {pendingFields.title && <span className="badge-pending">Pending Approval</span>}
        </label>
        <input
          type="text"
          className="form-input"
          value={formData.title}
          onChange={(e) => handleChange('title', e.target.value)}
          placeholder="e.g., Support for emotional and academic growth"
        />
        <button
          className="btn-primary"
          onClick={() => handleSubmitField('title')}
          disabled={submitting.title || pendingFields.title || !hasFieldChanged('title')}
        >
          {submitting.title ? 'Submitting...' : 'Submit for Approval'}
        </button>
      </div>

      {/* DESCRIPTION */}
      <div className="form-field-card">
        <label className="form-label">
          Description
          {pendingFields.description && <span className="badge-pending">Pending Approval</span>}
        </label>
        <textarea
          className="form-textarea"
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder="Describe the wellbeing program..."
          rows={4}
        />
        <button
          className="btn-primary"
          onClick={() => handleSubmitField('description')}
          disabled={submitting.description || pendingFields.description || !hasFieldChanged('description')}
        >
          {submitting.description ? 'Submitting...' : 'Submit for Approval'}
        </button>
      </div>

      {/* PILLARS */}
      <div className="form-field-card">
        <label className="form-label">
          Core Pillars
          {pendingFields.pillars && <span className="badge-pending">Pending Approval</span>}
        </label>
        <div style={{ marginBottom: '12px' }}>
          {(() => {
            const pillars = Array.isArray(formData.pillars) ? [...formData.pillars] : [];
            while (pillars.length < 3) {
              pillars.push('');
            }
            return pillars.map((pillar, idx) => (
              <div key={idx} className="input-group-row">
                <input
                  type="text"
                  value={pillar || ''}
                  onChange={(e) => {
                    const updated = [...pillars];
                    updated[idx] = e.target.value;
                    handleChange('pillars', updated.filter(p => p.trim() !== ''));
                  }}
                  placeholder={`Pillar ${idx + 1}`}
                  className="form-input"
                  style={{ flex: 1 }}
                />
                {idx >= 3 && (
                  <button
                    onClick={() => {
                      const updated = pillars.filter((_, i) => i !== idx);
                      handleChange('pillars', updated.filter(p => p.trim() !== ''));
                    }}
                    className="btn-icon-delete"
                  >
                    ✕
                  </button>
                )}
              </div>
            ));
          })()}
          <button
            onClick={() => {
              const updated = [...formData.pillars, ''];
              handleChange('pillars', updated);
            }}
            className="btn-secondary btn-add"
          >
            + Add Pillar
          </button>
        </div>
        <button
          className="btn-primary"
          onClick={() => handleSubmitField('pillars')}
          disabled={submitting.pillars || pendingFields.pillars || !hasFieldChanged('pillars')}
        >
          {submitting.pillars ? 'Submitting...' : 'Submit for Approval'}
        </button>
      </div>

      {/* SUPPORT PATHWAYS */}
      <div className="form-field-card">
        <label className="form-label">
          Support Pathways
          {pendingFields.supports && <span className="badge-pending">Pending Approval</span>}
        </label>
        <div style={{ marginBottom: '12px' }}>
          {(() => {
            const supports = Array.isArray(formData.supports) ? [...formData.supports] : [];
            while (supports.length < 3) {
              supports.push({ title: '', detail: '', icon: 'fa-star' });
            }
            return supports.map((support, idx) => (
              <div key={idx} className="nested-card">
                <div className="input-group-row">
                  <input
                    type="text"
                    value={support.title || ''}
                    onChange={(e) => {
                      const updated = [...supports];
                      updated[idx] = { ...updated[idx], title: e.target.value };
                      handleChange('supports', updated.filter(s => s.title.trim() !== ''));
                    }}
                    placeholder="Support Title"
                    className="form-input"
                    style={{ flex: 1 }}
                  />
                  {idx >= 3 && (
                    <button
                      onClick={() => {
                        const updated = supports.filter((_, i) => i !== idx);
                        handleChange('supports', updated.filter(s => s.title.trim() !== ''));
                      }}
                      className="btn-icon-delete"
                    >
                      ✕
                    </button>
                  )}
                </div>
                <textarea
                  value={support.detail || ''}
                  onChange={(e) => {
                    const updated = [...supports];
                    updated[idx] = { ...updated[idx], detail: e.target.value };
                    handleChange('supports', updated);
                  }}
                  placeholder="Support details..."
                  className="form-textarea"
                  rows={2}
                />
                <div style={{ marginBottom: '8px' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#718096', marginBottom: '6px' }}>
                    Icon
                  </label>
                  <select
                    value={support.icon || 'fa-star'}
                    onChange={(e) => {
                      const updated = [...supports];
                      updated[idx] = { ...updated[idx], icon: e.target.value };
                      handleChange('supports', updated);
                    }}
                    className="form-select"
                    style={{ 
                      fontFamily: "'Font Awesome 5 Free', sans-serif",
                      fontWeight: '900'
                    }}
                  >
                    {ICON_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  {support.icon && (
                    <div style={{ 
                      marginTop: '8px', 
                      padding: '12px', 
                      background: '#f8fafc', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      textAlign: 'center'
                    }}>
                      <i 
                        className={`fas ${support.icon}`} 
                        style={{ fontSize: '32px', color: '#4299e1' }}
                      />
                      <div style={{ 
                        fontSize: '11px', 
                        color: '#718096', 
                        marginTop: '6px',
                        fontWeight: '600'
                      }}>
                        Preview
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ));
          })()}
          <button
            onClick={() => {
              const updated = [...formData.supports, { title: '', detail: '', icon: 'fa-star' }];
              handleChange('supports', updated);
            }}
            className="btn-secondary btn-add"
          >
            + Add Support Pathway
          </button>
        </div>
        <button
          className="btn-primary"
          onClick={() => handleSubmitField('supports')}
          disabled={submitting.supports || pendingFields.supports || !hasFieldChanged('supports')}
        >
          {submitting.supports ? 'Submitting...' : 'Submit for Approval'}
        </button>
      </div>

      {/* IMAGES */}
      <div className="form-field-card">
        <label className="form-label">
          Wellbeing Images (3 slides)
          {pendingFields.images && <span className="badge-pending">Pending Approval</span>}
        </label>
        <div className="image-grid-3">
          {[0, 1, 2].map((imgIndex) => {
            const imgId = formData.images[imgIndex];
            const imgUrl = imgId ? mediaMap[imgId] : null;
            const key = `img-${imgIndex}`;
            return (
              <div key={imgIndex} className="image-upload-card">
                {imgUrl && (
                  <img
                    src={imgUrl}
                    alt={`Wellbeing ${imgIndex + 1}`}
                    className="image-preview"
                  />
                )}
                <div className="image-upload-footer">
                  <div className="image-info">
                    Image {imgIndex + 1} {imgId ? `(ID: ${imgId})` : '(empty)'}
                  </div>
                  <label className="btn-upload">
                    {imageUploading[key] 
                      ? `Uploading ${imageProgress[key] || 0}%`
                      : 'Upload Image'
                    }
                    <input
                      type="file"
                      onChange={(e) => handleImageUpload(imgIndex, e)}
                      disabled={imageUploading[key]}
                      accept="image/*"
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
              </div>
            );
          })}
        </div>
        <button
          className="btn-primary"
          onClick={() => handleSubmitField('images')}
          disabled={submitting.images || pendingFields.images || !hasFieldChanged('images')}
        >
          {submitting.images ? 'Submitting...' : 'Submit for Approval'}
        </button>
      </div>

      <div className="form-tip">
        <strong>💡 Tip:</strong> All changes require approval before appearing on the public website. Submit each field separately for better tracking.
      </div>
      </div>
    </div>
  );
}
